/**
 * One explicit state for the migration, instead of several booleans that can
 * disagree.
 *
 * The portal now has a state it never had before: the website is live and
 * healthy, and the transaction path is deliberately closed. AsetquSwap is
 * deployed, its window is already open, and it holds no ASETQU V2 until the
 * Safe's 3-of-5 funds it. A swap submitted in that state reverts on an empty
 * pool, so the button must be refused — but refusing it is not an error, and
 * every generic failure path in this app says something untrue about it:
 * "transaksi gagal" blames the chain, "saldo BNB tidak cukup" blames the
 * user's wallet, and a 503 page suggests the portal is down. It is none of
 * those. It is `PORTAL_READY_MIGRATION_PENDING`.
 *
 * Deriving one value rather than reading five is what stops two parts of the
 * page reaching different conclusions — the case that produces a disabled
 * button beside a message saying everything is ready.
 *
 * Nothing here authorises anything. `MIGRATION_READY` is what lets the UI
 * *offer* an action; whether that action succeeds is decided by the server's
 * own `claimable`, by the gas routes' MIGRATION_NOT_READY refusal, and last by
 * AsetquSwap itself. This module is the courtesy, not the control.
 */
import { ASETQU_TOKEN, CHAIN_ID, CLAIM_CONTRACT, LEGACY_TOKEN } from "./config";

export type MigrationState =
  /** Chain state has not come back yet. Nothing is known, so nothing is said. */
  | "LOADING"
  /** The wallet is connected to a different chain than this portal serves. */
  | "WRONG_NETWORK"
  /** The portal was deployed without an address it cannot work without. */
  | "CONFIG_ERROR"
  /** The chain could not be read. Distinct from "the answer was no". */
  | "RPC_ERROR"
  /** The owner has paused AsetquSwap. Funds are untouched. */
  | "PAUSED"
  /** Past SWAP_END. Nothing more can be migrated through this contract. */
  | "MIGRATION_ENDED"
  /**
   * The website is public and working; migration transactions are not open.
   *
   * Reached before SWAP_START, before the pool is funded, and before the
   * operator has turned on the launch control. All three are ordinary states of
   * a migration that has not started yet, and none of them is a fault.
   */
  | "PORTAL_READY_MIGRATION_PENDING"
  /** Everything the portal can check is satisfied; actions may be offered. */
  | "MIGRATION_READY";

/** The migration-wide half of `/api/eligibility`, independent of the wallet. */
export type MigrationInfo = {
  actionsEnabled: boolean;
  ready: boolean;
  poolFunded: boolean;
  requiredPoolRaw: string;
  swapPoolBalanceRaw: string | null;
  poolCoverageBps: number | null;
  reasons: string[];
};

export type MigrationStateInput = {
  /** Null until the first `/api/eligibility` response arrives. */
  migration: MigrationInfo | null;
  chainError: string | null;
  claimPaused: boolean | null;
  windowOpen: boolean | null;
  chainTimestamp: string | null;
  claimWindow: { start: string; end: string } | null;
  /** The chain the wallet is on, or null when no wallet is connected. */
  connectedChainId: number | null;
};

/** Reason codes the server sends. Mirrors internal/api/readiness.go. */
export const MIGRATION_NOT_READY = "MIGRATION_NOT_READY";

export function migrationState(input: MigrationStateInput): MigrationState {
  /*
    Configuration first, because it is the one failure the visitor cannot
    resolve and the only one that means the page itself is wrong. A portal
    missing any of these three cannot show a balance, cannot name a contract to
    verify, and must not pretend otherwise.
  */
  if (!CLAIM_CONTRACT || !LEGACY_TOKEN || !ASETQU_TOKEN) return "CONFIG_ERROR";

  /*
    Before anything read from the chain, because a wallet on the wrong network
    is being told about balances that are not the ones it holds. Only checked
    once a wallet is connected: a visitor reading the page has no network.
  */
  if (input.connectedChainId !== null && input.connectedChainId !== CHAIN_ID) {
    return "WRONG_NETWORK";
  }

  if (input.migration === null) return "LOADING";
  if (input.chainError !== null) return "RPC_ERROR";

  /*
    Ended before paused, and both before pending.

    After SWAP_END the contract is closed for good and the sentence a visitor
    needs is final; saying "sedang dipersiapkan" there would promise something
    that is never coming.
  */
  if (input.claimWindow !== null && input.chainTimestamp !== null) {
    if (BigInt(input.chainTimestamp) > BigInt(input.claimWindow.end)) {
      return "MIGRATION_ENDED";
    }
  }

  if (input.claimPaused === true) return "PAUSED";

  /*
    The server's own verdict, not a rule reimplemented here.

    `ready` folds together the launch control, the pool and the pause, and it is
    computed where the chain reads happen. Recomputing it from the parts in the
    browser is how the two ends drift into disagreeing about whether a button
    may be shown.
  */
  if (!input.migration.ready) return "PORTAL_READY_MIGRATION_PENDING";

  // Inside the window, funded, enabled: everything this page can establish.
  if (input.windowOpen !== true) return "PORTAL_READY_MIGRATION_PENDING";

  return "MIGRATION_READY";
}

/**
 * Whether the page may offer approve or swap.
 *
 * One function, used by every action, so that adding a state cannot leave one
 * button behind. A `false` here is never the last word — see the module note.
 */
export function migrationActionsAllowed(state: MigrationState): boolean {
  return state === "MIGRATION_READY";
}

export type MigrationNotice = { title: string; body: string };

/**
 * What the visitor reads while the transaction path is closed.
 *
 * Concise, and specific about which of the two halves is which: the portal is
 * live, the pool is being prepared. Deliberately says nothing about the Safe,
 * the signers or the funding transaction — that is operational detail, and a
 * holder reading it would have no way to act on it.
 */
export const MIGRATION_PENDING_NOTICE: MigrationNotice = {
  title: "Pool migrasi sedang dipersiapkan",
  body:
    "Portal migrasi sudah aktif. Transaksi akan tersedia setelah pool ASETQU " +
    "baru selesai didanai.",
};
